import { useState } from 'react';
import { timeInCity, fmtHM, verdictFor, tierFor, fmtPrice } from '../data/layoverConfig';
import { trackWidgetEvent } from '../lib/widgetAnalytics';
import { WHATSAPP_NUMBER } from './chatLabels';

const toMin = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };

const T = {
  en: {
    tour: { sawFull10: 'Kadıköy & Moda, with the Old City as an option at 10h+', saw: 'Kadıköy food market & Moda shoreline', '7h': 'Old City essentials on foot (no paid entries)', '8h': 'Old City walking circuit + Grand Bazaar', '10h': 'Full Old City, optional Bosphorus ferry' },
    wa: (res, land, dep) => `Hello Viaturkey, layover at ${res.airport}: landing ${land}, departing ${dep}, ${res.pax} travellers. Can you suggest an itinerary?`,
    airport: 'Airport', ist: 'Istanbul Airport (IST)', saw: 'Sabiha Gökçen (SAW)', landing: 'Landing time', departure: 'Departure time', travellers: 'Travellers', calc: 'Calculate',
    layover: 'Layover:', inCity: 'Time in the city:',
    breakdown: (cfg, transfers) => <>After {cfg.arrival_min} min for passport control, {transfers} of transfers and our {fmtHM(cfg.return_buffer_min)} return buffer.</>,
    belowMin: (cfg) => <><strong>Under {cfg.min_layover_hours} hours it doesn't work, and we won't do it.</strong> The margin is too thin once passport control, transfers and the return buffer are counted — use an airport lounge instead.</>,
    noTime: <><strong>Not enough time to leave the airport safely.</strong> Use an airport lounge instead — we do not offer a tour for this connection.</>,
    short: 'Short itinerary', full: 'Full itinerary', sawShort: ' — Kadıköy is the right call from SAW with this much time.',
    priceLabel: 'Indicative price:', priceRest: ' — confirmed when we plan your itinerary. Entrance fees and food are not included.',
    waBtn: 'Send these times on WhatsApp', note: 'Estimates only. Traffic and queues vary.',
  },
  ar: {
    tour: { sawFull10: 'قاضي كوي ومودا، مع المدينة القديمة كخيار ابتداءً من 10 ساعات', saw: 'سوق قاضي كوي للطعام وواجهة مودا البحرية', '7h': 'أساسيات المدينة القديمة سيراً (دون مواقع مدفوعة)', '8h': 'جولة السير في المدينة القديمة + البازار الكبير', '10h': 'المدينة القديمة كاملة، مع عبّارة اختيارية في البوسفور' },
    wa: (res, land, dep) => `مرحباً فياتركي، لديّ توقف في ${res.airport}: الهبوط ${land}، والمغادرة ${dep}، وعدد المسافرين ${res.pax}. هل يمكنكم اقتراح برنامج؟`,
    airport: 'المطار', ist: 'مطار إسطنبول (IST)', saw: 'مطار صبيحة كوكجن (SAW)', landing: 'وقت الهبوط', departure: 'وقت المغادرة', travellers: 'عدد المسافرين', calc: 'احسب',
    layover: 'مدة التوقف:', inCity: 'الوقت في المدينة:',
    breakdown: (cfg, transfers) => <>بعد {cfg.arrival_min} دقيقة لإجراءات الجوازات، و{transfers} للنقل، وهامش العودة لدينا {fmtHM(cfg.return_buffer_min, 'ar')}.</>,
    belowMin: (cfg) => <><strong>تحت {cfg.min_layover_hours} ساعات لا يستقيم الأمر، ولن نقوم به.</strong> فالهامش ضيق جداً بعد احتساب إجراءات الجوازات والنقل وهامش العودة — استخدم صالة انتظار في المطار بدلاً من ذلك.</>,
    noTime: <><strong>لا يوجد وقت كافٍ لمغادرة المطار بأمان.</strong> استخدم صالة انتظار في المطار بدلاً من ذلك — لا نعرض جولة لهذا التوقف.</>,
    short: 'برنامج قصير', full: 'برنامج كامل', sawShort: ' — قاضي كوي هي الخيار الصحيح من مطار SAW مع هذا الوقت.',
    priceLabel: 'السعر التقريبي:', priceRest: ' — يُؤكَّد عند تخطيط برنامجك. لا يشمل رسوم الدخول والطعام.',
    waBtn: 'أرسل هذه الأوقات عبر واتساب', note: 'تقديرات فقط. تختلف حركة المرور والطوابير.',
  },
};

export function LayoverCalculator({ cfg, lang = 'en' }) {
  const t = T[lang] || T.en;
  const [airport, setAirport] = useState('IST');
  const [land, setLand] = useState('');
  const [dep, setDep] = useState('');
  const [pax, setPax] = useState(2);
  const [res, setRes] = useState(null);

  const calc = (e) => {
    e.preventDefault();
    if (!land || !dep) return;
    let layover = toMin(dep) - toMin(land);
    if (layover <= 0) layover += 24 * 60;
    const city = timeInCity(cfg, airport, layover);
    const belowMin = layover < cfg.min_layover_hours * 60;
    const verdict = belowMin ? 'none' : verdictFor(city);
    const key = layover >= 600 ? '10h' : layover >= 480 ? '8h' : '7h';
    const tier = tierFor(cfg, key, pax);
    const price = tier == null ? null : fmtPrice(cfg, tier);
    const r = { layover, city, verdict, key, price, airport, pax, belowMin };
    setRes(r);
    trackWidgetEvent('layover_calculated', { widget: 'layover', airport, verdict, layover_min: layover, city_min: city });
  };

  const tourName = res && (res.airport === 'SAW'
    ? (res.verdict === 'full' && res.layover >= 600 ? t.tour.sawFull10 : t.tour.saw)
    : t.tour[res.key]);
  const waText = res && t.wa(res, land, dep);

  return (
    <div className="lo-calc" data-testid="layover-calculator">
      <form onSubmit={calc} className="lo-calc-form">
        <label>{t.airport}
          <select value={airport} onChange={e => setAirport(e.target.value)} data-testid="lo-calc-airport">
            <option value="IST">{t.ist}</option>
            <option value="SAW">{t.saw}</option>
          </select>
        </label>
        <label>{t.landing}<input type="time" value={land} onChange={e => setLand(e.target.value)} required data-testid="lo-calc-landing" /></label>
        <label>{t.departure}<input type="time" value={dep} onChange={e => setDep(e.target.value)} required data-testid="lo-calc-departure" /></label>
        <label>{t.travellers}<input type="number" min="1" max={cfg.group_max} value={pax} onChange={e => setPax(Number(e.target.value))} data-testid="lo-calc-pax" /></label>
        <button type="submit" className="btn btn-primary" data-testid="lo-calc-submit">{t.calc}</button>
      </form>
      {res && (
        <div className={`lo-calc-result lo-verdict-${res.verdict}`} data-testid="lo-calc-result">
          <p><strong>{t.layover}</strong> {fmtHM(res.layover, lang)} · <strong>{t.inCity}</strong> {fmtHM(Math.max(res.city, 0), lang)}</p>
          <p className="lo-calc-breakdown">{t.breakdown(cfg, fmtHM(2 * (res.airport === 'SAW' ? cfg.saw_kadikoy_min : cfg.ist_oldcity_min), lang))}</p>
          {res.verdict === 'none' ? (
            <p data-testid="lo-calc-verdict">{res.belowMin && res.city >= 60 ? t.belowMin(cfg) : t.noTime}</p>
          ) : (
            <>
              <p data-testid="lo-calc-verdict"><strong>{res.verdict === 'short' ? t.short : t.full}:</strong> {tourName}{res.airport === 'SAW' && res.verdict === 'short' ? t.sawShort : ''}</p>
              {res.price && <p data-testid="lo-calc-price"><strong>{t.priceLabel}</strong> {res.price}{t.priceRest}</p>}
              <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(waText)}`} target="_blank" rel="noopener noreferrer" className="btn btn-primary" onClick={() => trackWidgetEvent('layover_cta_clicked', { widget: 'layover', airport: res.airport, verdict: res.verdict, channel: 'whatsapp' })} data-testid="lo-calc-whatsapp">
                <i className="fab fa-whatsapp"></i> {t.waBtn}
              </a>
            </>
          )}
          <p className="lo-calc-note"><em>{t.note}</em></p>
        </div>
      )}
    </div>
  );
}
